export type AssistantSceneStateValue = string | number | boolean;

type EnumControlPolicy = {
  readonly kind: "enum";
  readonly values: readonly string[];
  readonly defaultValue?: string;
};

type NumberControlPolicy = {
  readonly kind: "number";
  readonly min: number;
  readonly max: number;
  readonly integer?: boolean;
  readonly defaultValue?: number;
};

type BooleanControlPolicy = {
  readonly kind: "boolean";
  readonly defaultValue?: boolean;
};

type ControlPolicy = EnumControlPolicy | NumberControlPolicy | BooleanControlPolicy;

type SceneStatePolicy = {
  readonly selectedItemIds?: readonly string[];
  readonly placementItemIds?: readonly string[];
  readonly placementTargetIds?: readonly string[];
  readonly progressControlIds?: readonly string[];
  readonly controlValues?: Readonly<Record<string, ControlPolicy>>;
};

const booleanControl = { kind: "boolean" } as const;
const enumControl = (...values: string[]) => ({ kind: "enum", values }) as const;
const integerControl = (min: number, max: number) => ({ kind: "number", min, max, integer: true }) as const;
const numberControl = (min: number, max: number) => ({ kind: "number", min, max }) as const;
const withDefault = <T extends ControlPolicy>(policy: T, defaultValue: AssistantSceneStateValue) => ({
  ...policy,
  defaultValue
}) as T;

const c1ClassificationItems = [
  "sprout-card", "peach", "swallow-card", "cuckoo", "fruit", "yellow-leaf",
  "geese", "insects", "melt", "snow", "rain", "rock"
] as const;
const c1SequenceItems = [
  "melt-sprout", "swallow", "cuckoo-fruit", "ripe-leaf", "geese-insects", "winter"
] as const;
const c2ObservationItems = [
  "stage-0", "stage-1", "stage-2", "stage-3",
  "observe:peach"
] as const;
const factorIds = ["latitude", "longitude", "elevation", "era"] as const;
const factorLabels: Readonly<Record<(typeof factorIds)[number], string>> = {
  latitude: "纬度",
  longitude: "经度（海陆差异）",
  elevation: "高下",
  era: "古今差异"
};
const productionItems = ["sow", "zone", "forest", "introduce", "pest", "mountain"] as const;

export const F01_ASSISTANT_CONTROL_IDS = {
  corePhenology: "f01.core.phenology.option",
  coreLongitude: "f01.core.longitude.option",
  coreElevation: "f01.core.elevation.option",
  coreLatitude: "f01.core.latitude.option",
  structure: "f01.structure.option",
  methods: "f01.methods.option",
  case1962: "f01.case-1962.option",
  attitude: "f01.attitude.option"
} as const;

export const F02_ASSISTANT_CONTROL_IDS = {
  seasonP07: "f02.evidence.season.P07",
  seasonP08: "f02.evidence.season.P08",
  seasonP09: "f02.evidence.season.P09",
  seasonP10: "f02.evidence.season.P10",
  lossP04: "f02.evidence.loss.P04",
  lossP05: "f02.evidence.loss.P05",
  lossP11: "f02.evidence.loss.P11",
  lossP12: "f02.evidence.loss.P12",
  evidenceAttempted: "f02.evidence.attempted",
  seasonSlot1: "f02.rewrite.season.slot1",
  seasonSlot2: "f02.rewrite.season.slot2",
  seasonSlot3: "f02.rewrite.season.slot3",
  lossSlot1: "f02.rewrite.loss.slot1",
  lossSlot2: "f02.rewrite.loss.slot2",
  lossSlot3: "f02.rewrite.loss.slot3",
  rewriteAttempted: "f02.rewrite.attempted"
} as const;

export const F02_ASSISTANT_PHRASE_IDS = {
  seasonDifference: "season-difference",
  seasonCondition: "season-condition",
  seasonChange: "season-change",
  lossReference: "loss-reference",
  lossScope: "loss-scope",
  lossOutcome: "loss-outcome"
} as const;

const commonExperimentControls = {
  operated: booleanControl,
  factorMatched: booleanControl,
  relationDone: booleanControl,
  boundaryDone: booleanControl
} as const;

export const ASSISTANT_SCENE_STATE_POLICIES = {
  "C1-00": {
    selectedItemIds: ["spring", "summer", "autumn", "winter"],
    controlValues: {
      seasonTimelinePosition: withDefault(numberControl(0, 100), 0),
      seasonIndex: withDefault(integerControl(0, 3), 0)
    }
  },
  "C1-01": {
    placementItemIds: c1ClassificationItems,
    placementTargetIds: ["phenology", "seasonal", "event"]
  },
  "C1-02": { selectedItemIds: c1SequenceItems },
  "C1-03": {},
  "C2-00": {
    selectedItemIds: c2ObservationItems,
    controlValues: {
      timelineProgress: withDefault(numberControl(0, 100), 0),
      stage: withDefault(integerControl(0, 3), 0),
      focusedKind: withDefault(enumControl("peach", "bird", "insect", "weather"), "peach")
    }
  },
  "C2-01": {
    selectedItemIds: ["peach-tree", "swallow", "thermometer", "hygrometer"]
  },
  "C2-03": {},
  "C3-00": {
    controlValues: { question: enumControl("factors", "latitude-only") }
  },
  "C3-01": {
    progressControlIds: [
      "seen:early-spring", "seen:late-spring",
      "operated", "factorMatched", "relationDone", "boundaryDone"
    ],
    controlValues: {
      latitudeSpringPosition: withDefault(numberControl(0, 100), 0),
      latitudeSeason: withDefault(enumControl("early-spring", "late-spring"), "early-spring"),
      latitudeCity: withDefault(enumControl("nanjing", "beijing"), "nanjing"),
      "seen:early-spring": booleanControl,
      "seen:late-spring": booleanControl,
      ...commonExperimentControls
    }
  },
  "C3-02": {
    progressControlIds: [
      "seen:beijing-inland", "seen:dalian-coast",
      "operated", "factorMatched", "relationDone", "boundaryDone"
    ],
    controlValues: {
      longitudeWindPosition: withDefault(numberControl(0, 100), 0),
      longitudePlace: withDefault(enumControl("beijing-inland", "dalian-coast"), "beijing-inland"),
      "seen:beijing-inland": booleanControl,
      "seen:dalian-coast": booleanControl,
      ...commonExperimentControls
    }
  },
  "C3-03": {
    progressControlIds: [
      "seen:foot", "seen:middle", "seen:top", "seen:spring-summer", "seen:autumn",
      "operated", "factorMatched", "relationDone", "boundaryDone", "showInversion"
    ],
    controlValues: {
      elevationLevel: withDefault(enumControl("foot", "middle", "top"), "foot"),
      elevationSeason: withDefault(enumControl("spring-summer", "autumn"), "spring-summer"),
      "seen:foot": booleanControl,
      "seen:middle": booleanControl,
      "seen:top": booleanControl,
      "seen:spring-summer": booleanControl,
      "seen:autumn": booleanControl,
      showInversion: booleanControl,
      ...commonExperimentControls
    }
  },
  "C3-04": {
    progressControlIds: [
      "seen:1741-1750", "seen:1921-1930",
      "operated", "factorMatched", "relationDone", "boundaryDone"
    ],
    controlValues: {
      eraPeriod: withDefault(enumControl("1741-1750", "1921-1930"), "1741-1750"),
      eraStarted: booleanControl,
      "seen:1741-1750": booleanControl,
      "seen:1921-1930": booleanControl,
      ...commonExperimentControls
    }
  },
  "C3-05": {
    selectedItemIds: factorIds.map((factorId) => `mapped:${factorId}`),
    placementItemIds: factorIds.flatMap((factorId) => [`claim:${factorId}`, `boundary:${factorId}`]),
    placementTargetIds: factorIds
  },
  "C4-01": {
    placementItemIds: productionItems,
    placementTargetIds: productionItems
  },
  "C4-03": {},
  F01: {
    controlValues: {
      [F01_ASSISTANT_CONTROL_IDS.corePhenology]: integerControl(0, 5),
      [F01_ASSISTANT_CONTROL_IDS.coreLongitude]: integerControl(0, 5),
      [F01_ASSISTANT_CONTROL_IDS.coreElevation]: integerControl(0, 5),
      [F01_ASSISTANT_CONTROL_IDS.coreLatitude]: integerControl(0, 5),
      [F01_ASSISTANT_CONTROL_IDS.structure]: integerControl(0, 3),
      [F01_ASSISTANT_CONTROL_IDS.methods]: integerControl(0, 3),
      [F01_ASSISTANT_CONTROL_IDS.case1962]: integerControl(0, 3),
      [F01_ASSISTANT_CONTROL_IDS.attitude]: integerControl(0, 3)
    }
  },
  F02: {
    controlValues: {
      [F02_ASSISTANT_CONTROL_IDS.seasonP07]: booleanControl,
      [F02_ASSISTANT_CONTROL_IDS.seasonP08]: booleanControl,
      [F02_ASSISTANT_CONTROL_IDS.seasonP09]: booleanControl,
      [F02_ASSISTANT_CONTROL_IDS.seasonP10]: booleanControl,
      [F02_ASSISTANT_CONTROL_IDS.lossP04]: booleanControl,
      [F02_ASSISTANT_CONTROL_IDS.lossP05]: booleanControl,
      [F02_ASSISTANT_CONTROL_IDS.lossP11]: booleanControl,
      [F02_ASSISTANT_CONTROL_IDS.lossP12]: booleanControl,
      [F02_ASSISTANT_CONTROL_IDS.evidenceAttempted]: booleanControl,
      [F02_ASSISTANT_CONTROL_IDS.seasonSlot1]: enumControl(
        F02_ASSISTANT_PHRASE_IDS.seasonDifference,
        F02_ASSISTANT_PHRASE_IDS.seasonCondition,
        F02_ASSISTANT_PHRASE_IDS.seasonChange
      ),
      [F02_ASSISTANT_CONTROL_IDS.seasonSlot2]: enumControl(
        F02_ASSISTANT_PHRASE_IDS.seasonDifference,
        F02_ASSISTANT_PHRASE_IDS.seasonCondition,
        F02_ASSISTANT_PHRASE_IDS.seasonChange
      ),
      [F02_ASSISTANT_CONTROL_IDS.seasonSlot3]: enumControl(
        F02_ASSISTANT_PHRASE_IDS.seasonDifference,
        F02_ASSISTANT_PHRASE_IDS.seasonCondition,
        F02_ASSISTANT_PHRASE_IDS.seasonChange
      ),
      [F02_ASSISTANT_CONTROL_IDS.lossSlot1]: enumControl(
        F02_ASSISTANT_PHRASE_IDS.lossReference,
        F02_ASSISTANT_PHRASE_IDS.lossScope,
        F02_ASSISTANT_PHRASE_IDS.lossOutcome
      ),
      [F02_ASSISTANT_CONTROL_IDS.lossSlot2]: enumControl(
        F02_ASSISTANT_PHRASE_IDS.lossReference,
        F02_ASSISTANT_PHRASE_IDS.lossScope,
        F02_ASSISTANT_PHRASE_IDS.lossOutcome
      ),
      [F02_ASSISTANT_CONTROL_IDS.lossSlot3]: enumControl(
        F02_ASSISTANT_PHRASE_IDS.lossReference,
        F02_ASSISTANT_PHRASE_IDS.lossScope,
        F02_ASSISTANT_PHRASE_IDS.lossOutcome
      ),
      [F02_ASSISTANT_CONTROL_IDS.rewriteAttempted]: booleanControl
    }
  },
  F03: {}
} as const satisfies Readonly<Record<string, SceneStatePolicy>>;

export type AssistantStateSceneId = keyof typeof ASSISTANT_SCENE_STATE_POLICIES;

type SceneStateLabels = {
  readonly items?: Readonly<Record<string, string>>;
  readonly controls?: Readonly<Record<string, string>>;
  readonly values?: Readonly<Record<string, Readonly<Record<string, string>>>>;
};

const ASSISTANT_SCENE_STATE_LABELS: Readonly<Partial<Record<AssistantStateSceneId, SceneStateLabels>>> = {
  "C1-00": {
    items: { spring: "春季", summer: "夏季", autumn: "秋季", winter: "冬季" },
    controls: { seasonTimelinePosition: "全年连续时间轴位置", seasonIndex: "当前季节" },
    values: { seasonIndex: { "0": "春季", "1": "夏季", "2": "秋季", "3": "冬季" } }
  },
  "C1-01": {
    items: {
      "sprout-card": "草木萌发卡", peach: "桃花盛开卡", "swallow-card": "燕子归来卡",
      cuckoo: "布谷鸟到来卡", fruit: "果实成熟卡", "yellow-leaf": "树叶变黄飘落卡",
      geese: "北雁南飞卡", insects: "昆虫销声匿迹卡", melt: "冰雪消融卡",
      snow: "风雪载途卡", rain: "短时暴雨卡", rock: "山石滚落卡"
    }
  },
  "C1-02": {
    items: {
      "melt-sprout": "冰雪融化、草木萌发", swallow: "燕子归来",
      "cuckoo-fruit": "布谷鸟到来、植物孕育果实", "ripe-leaf": "果实成熟、树叶变黄",
      "geese-insects": "北雁南飞、昆虫销声匿迹", winter: "衰草连天、风雪将至"
    }
  },
  "C2-00": {
    items: {
      "stage-0": "观察时间点一", "stage-1": "观察时间点二",
      "stage-2": "观察时间点三", "stage-3": "观察时间点四",
      "observe:peach": "桃树观察", "observe:bird": "候鸟观察",
      "observe:insect": "昆虫观察", "observe:weather": "气象仪器观察"
    },
    controls: {
      timelineProgress: "连续时间尺进度", stage: "当前观察时间点", focusedKind: "当前观察对象"
    },
    values: {
      stage: { "0": "观察时间点一", "1": "观察时间点二", "2": "观察时间点三", "3": "观察时间点四" },
      focusedKind: { peach: "桃树", bird: "候鸟", insect: "昆虫", weather: "气象仪器" }
    }
  },
  "C2-01": {
    items: {
      "peach-tree": "桃树", swallow: "燕子", thermometer: "温度计", hygrometer: "湿度计"
    }
  },
  "C3-00": {
    controls: { question: "当前研究问题" },
    values: { question: { factors: "寻找影响物候来临迟早的多种因素", "latitude-only": "只研究纬度造成的南北物候差异" } }
  },
  "C3-01": {
    items: {
      "seen:early-spring": "已观察到早春南京开花", "seen:late-spring": "已观察到晚春北京开花",
      operated: "已走完早春到晚春进度条", factorMatched: "已匹配纬度因素",
      relationDone: "已判断纬度关系", boundaryDone: "已核对纬度结论边界"
    },
    controls: {
      latitudeSpringPosition: "早春到晚春进度", latitudeSeason: "当前春季阶段",
      latitudeCity: "指示杆靠近的城市",
      "seen:early-spring": "是否到过早春", "seen:late-spring": "是否到过晚春",
      operated: "纬度控件是否走完", factorMatched: "纬度因素是否匹配",
      relationDone: "纬度关系是否判断", boundaryDone: "纬度边界是否核对"
    },
    values: {
      latitudeCity: { nanjing: "南京", beijing: "北京" },
      latitudeSeason: { "early-spring": "早春：南京开花", "late-spring": "晚春：北京开花" }
    }
  },
  "C3-02": {
    items: {
      "seen:beijing-inland": "已查看北京一端", "seen:dalian-coast": "已查看大连一端",
      operated: "已比较北京与大连", factorMatched: "已匹配经度（海陆差异）因素",
      relationDone: "已判断海陆关系", boundaryDone: "已核对海陆结论边界"
    },
    controls: {
      longitudeWindPosition: "北京到大连进度", longitudePlace: "当前查看的城市",
      "seen:beijing-inland": "是否到过北京一端",
      "seen:dalian-coast": "是否到过大连一端", operated: "海陆控件是否走完",
      factorMatched: "经度因素是否匹配", relationDone: "海陆关系是否判断",
      boundaryDone: "海陆边界是否核对"
    },
    values: { longitudePlace: { "beijing-inland": "内陆北京", "dalian-coast": "沿海大连" } }
  },
  "C3-03": {
    items: {
      "seen:foot": "已比较山脚", "seen:middle": "已比较山腰", "seen:top": "已比较山顶",
      "seen:spring-summer": "已比较春夏模式", "seen:autumn": "已比较秋季模式",
      operated: "已走完海拔语义控件", factorMatched: "已匹配高下因素",
      relationDone: "已判断海拔关系", boundaryDone: "已核对海拔结论边界",
      showInversion: "已展开逆温例外"
    },
    controls: {
      elevationLevel: "当前山地位置", elevationSeason: "当前季节模式",
      "seen:foot": "是否比较过山脚", "seen:middle": "是否比较过山腰", "seen:top": "是否比较过山顶",
      "seen:spring-summer": "是否比较过春夏", "seen:autumn": "是否比较过秋季",
      operated: "海拔控件是否走完", factorMatched: "高下因素是否匹配",
      relationDone: "海拔关系是否判断", boundaryDone: "海拔边界是否核对",
      showInversion: "是否展开逆温例外"
    },
    values: {
      elevationLevel: { foot: "山脚", middle: "山腰", top: "山顶" },
      elevationSeason: { "spring-summer": "春夏抽青开花", autumn: "秋季落叶" }
    }
  },
  "C3-04": {
    items: {
      "seen:1741-1750": "已比较1741-1750十年平均", "seen:1921-1930": "已比较1921-1930十年平均",
      operated: "已走完古今语义控件", factorMatched: "已匹配古今因素",
      relationDone: "已判断古今关系", boundaryDone: "已核对九天平均差异的边界"
    },
    controls: {
      eraPeriod: "当前十年平均时段", eraStarted: "是否已点开始实验",
      "seen:1741-1750": "是否比较过1741-1750",
      "seen:1921-1930": "是否比较过1921-1930", operated: "古今控件是否走完",
      factorMatched: "古今因素是否匹配", relationDone: "古今关系是否判断",
      boundaryDone: "古今边界是否核对"
    },
    values: { eraPeriod: { "1741-1750": "1741-1750年春季物候到来平均时间", "1921-1930": "1921-1930年的同一时间" } }
  },
  "C3-05": {
    items: Object.fromEntries(factorIds.flatMap((factorId) => [
      [`claim:${factorId}`, `${factorLabels[factorId]}结论`],
      [`boundary:${factorId}`, `${factorLabels[factorId]}边界条件`],
      [`mapped:${factorId}`, `${factorLabels[factorId]}整行映射已核验`]
    ]))
  },
  "C4-01": {
    items: {
      sow: "播种时机用途卡", zone: "作物区划用途卡", forest: "造林采种用途卡",
      introduce: "植物引种用途卡", pest: "虫害防治用途卡", mountain: "山区综合调查用途卡"
    }
  },
  F01: {
    items: {
      "item.f01.core": "核心概念填空", "item.f01.structure": "行文结构题",
      "item.f01.methods": "说明方法题", "item.f01.case-1962": "1962年案例题",
      "item.f01.attitude": "科学态度题"
    },
    controls: {
      [F01_ASSISTANT_CONTROL_IDS.corePhenology]: "核心概念第1题当前词库项",
      [F01_ASSISTANT_CONTROL_IDS.coreLongitude]: "核心概念第2题当前词库项",
      [F01_ASSISTANT_CONTROL_IDS.coreElevation]: "核心概念第3题当前词库项",
      [F01_ASSISTANT_CONTROL_IDS.coreLatitude]: "核心概念第4题当前词库项",
      [F01_ASSISTANT_CONTROL_IDS.structure]: "行文结构题当前选项",
      [F01_ASSISTANT_CONTROL_IDS.methods]: "说明方法题当前选项",
      [F01_ASSISTANT_CONTROL_IDS.case1962]: "1962年案例题当前选项",
      [F01_ASSISTANT_CONTROL_IDS.attitude]: "科学态度题当前选项"
    },
    values: {
      [F01_ASSISTANT_CONTROL_IDS.corePhenology]: { "0": "物候", "1": "物候学", "2": "纬度", "3": "经度", "4": "高下", "5": "古今" },
      [F01_ASSISTANT_CONTROL_IDS.coreLongitude]: { "0": "物候", "1": "物候学", "2": "纬度", "3": "经度", "4": "高下", "5": "古今" },
      [F01_ASSISTANT_CONTROL_IDS.coreElevation]: { "0": "物候", "1": "物候学", "2": "纬度", "3": "经度", "4": "高下", "5": "古今" },
      [F01_ASSISTANT_CONTROL_IDS.coreLatitude]: { "0": "物候", "1": "物候学", "2": "纬度", "3": "经度", "4": "高下", "5": "古今" },
      [F01_ASSISTANT_CONTROL_IDS.structure]: {
        "0": "引出概念→说明农业价值→分析四个因素→研究意义",
        "1": "解释定义→列举四季→农业应用→总结号召",
        "2": "四季景观→分析因素→观测失误→界定学科",
        "3": "农谚→四个因素→播种实例→学科归属"
      },
      [F01_ASSISTANT_CONTROL_IDS.methods]: {
        "0": "举例子、列数字、作比较",
        "1": "作诠释、引用、打比方",
        "2": "分类别、列数字、摹状貌",
        "3": "下定义、作比较、引用"
      },
      [F01_ASSISTANT_CONTROL_IDS.case1962]: {
        "0": "气候固定不变，损害只由品种不耐寒造成",
        "1": "物候观测反映气候并为调整播种、减少可能损失提供参考",
        "2": "物候观测没有实际参考价值",
        "3": "北方春季低温完全不可预判"
      },
      [F01_ASSISTANT_CONTROL_IDS.attitude]: {
        "0": "全盘否定传统经验",
        "1": "辩证吸收传统经验并发展为系统科学研究",
        "2": "照搬古代经验并拒绝科学观测",
        "3": "传统农谚完全等同现代物候学"
      }
    }
  },
  F02: {
    items: {
      "item.f02.classify": "判断AI草稿问题类型", "item.f02.evidence": "选择课文证据",
      "item.f02.rewrite": "修订AI草稿", "item.f02.done": "完成审校"
    },
    controls: {
      [F02_ASSISTANT_CONTROL_IDS.seasonP07]: "季节差异草稿是否勾选P07",
      [F02_ASSISTANT_CONTROL_IDS.seasonP08]: "季节差异草稿是否勾选P08",
      [F02_ASSISTANT_CONTROL_IDS.seasonP09]: "季节差异草稿是否勾选P09",
      [F02_ASSISTANT_CONTROL_IDS.seasonP10]: "季节差异草稿是否勾选P10",
      [F02_ASSISTANT_CONTROL_IDS.lossP04]: "农业损失草稿是否勾选P04",
      [F02_ASSISTANT_CONTROL_IDS.lossP05]: "农业损失草稿是否勾选P05",
      [F02_ASSISTANT_CONTROL_IDS.lossP11]: "农业损失草稿是否勾选P11",
      [F02_ASSISTANT_CONTROL_IDS.lossP12]: "农业损失草稿是否勾选P12",
      [F02_ASSISTANT_CONTROL_IDS.evidenceAttempted]: "是否已提交本轮证据核验",
      [F02_ASSISTANT_CONTROL_IDS.seasonSlot1]: "季节差异修订第1槽",
      [F02_ASSISTANT_CONTROL_IDS.seasonSlot2]: "季节差异修订第2槽",
      [F02_ASSISTANT_CONTROL_IDS.seasonSlot3]: "季节差异修订第3槽",
      [F02_ASSISTANT_CONTROL_IDS.lossSlot1]: "农业损失修订第1槽",
      [F02_ASSISTANT_CONTROL_IDS.lossSlot2]: "农业损失修订第2槽",
      [F02_ASSISTANT_CONTROL_IDS.lossSlot3]: "农业损失修订第3槽",
      [F02_ASSISTANT_CONTROL_IDS.rewriteAttempted]: "是否已提交本轮修订顺序"
    },
    values: {
      [F02_ASSISTANT_CONTROL_IDS.seasonSlot1]: {
        [F02_ASSISTANT_PHRASE_IDS.seasonDifference]: "南北物候差异的日数",
        [F02_ASSISTANT_PHRASE_IDS.seasonCondition]: "会因季节不同",
        [F02_ASSISTANT_PHRASE_IDS.seasonChange]: "而变化"
      },
      [F02_ASSISTANT_CONTROL_IDS.seasonSlot2]: {
        [F02_ASSISTANT_PHRASE_IDS.seasonDifference]: "南北物候差异的日数",
        [F02_ASSISTANT_PHRASE_IDS.seasonCondition]: "会因季节不同",
        [F02_ASSISTANT_PHRASE_IDS.seasonChange]: "而变化"
      },
      [F02_ASSISTANT_CONTROL_IDS.seasonSlot3]: {
        [F02_ASSISTANT_PHRASE_IDS.seasonDifference]: "南北物候差异的日数",
        [F02_ASSISTANT_PHRASE_IDS.seasonCondition]: "会因季节不同",
        [F02_ASSISTANT_PHRASE_IDS.seasonChange]: "而变化"
      },
      [F02_ASSISTANT_CONTROL_IDS.lossSlot1]: {
        [F02_ASSISTANT_PHRASE_IDS.lossReference]: "物候资料可为农时和播种日期等提供参考",
        [F02_ASSISTANT_PHRASE_IDS.lossScope]: "有些损失",
        [F02_ASSISTANT_PHRASE_IDS.lossOutcome]: "可能因此避免或减轻"
      },
      [F02_ASSISTANT_CONTROL_IDS.lossSlot2]: {
        [F02_ASSISTANT_PHRASE_IDS.lossReference]: "物候资料可为农时和播种日期等提供参考",
        [F02_ASSISTANT_PHRASE_IDS.lossScope]: "有些损失",
        [F02_ASSISTANT_PHRASE_IDS.lossOutcome]: "可能因此避免或减轻"
      },
      [F02_ASSISTANT_CONTROL_IDS.lossSlot3]: {
        [F02_ASSISTANT_PHRASE_IDS.lossReference]: "物候资料可为农时和播种日期等提供参考",
        [F02_ASSISTANT_PHRASE_IDS.lossScope]: "有些损失",
        [F02_ASSISTANT_PHRASE_IDS.lossOutcome]: "可能因此避免或减轻"
      }
    }
  }
};

const ASSISTANT_RULE_LABELS: Readonly<Record<string, string>> = {
  "c1.process.observe": "完成四季长卷观察",
  "c1.classify.biological": "辨明物候现象",
  "c1.classify.weather": "辨明季节气象背景",
  "c1.classify.incidental": "辨明偶发自然事件",
  "c1.timeline.sequence": "复原四季物候时序",
  "c1.process.verify": "核验第一章课文证据",
  "c1.process.journal": "写入第一章观察册",
  "c2.process.observe": "走完四个时间点并记录桃树观察",
  "c2.instrument.peach": "确认桃树属于活的仪器",
  "c2.instrument.swallow": "确认燕子属于活的仪器",
  "c2.process.read-case": "阅读1962年农业案例",
  "c2.process.journal": "写入第二章观察册",
  "c3.latitude.operated": "走完早春到晚春的纬度观察",
  "c3.latitude.mastery": "完成纬度关系与结论边界核验",
  "c3.latitude.evidence": "查看纬度课文证据P07",
  "c3.longitude.operated": "比较北京与大连",
  "c3.longitude.mastery": "完成海陆关系与结论边界核验",
  "c3.longitude.evidence": "查看海陆课文证据P08",
  "c3.altitude.operated": "走完山脚、山腰、山顶与季节比较",
  "c3.altitude.mastery": "完成高下规律与逆温边界核验",
  "c3.altitude.evidence": "查看高下课文证据P09",
  "c3.time.operated": "走完两组十年平均比较",
  "c3.time.mastery": "完成古今差异与九天平均边界核验",
  "c3.time.evidence": "查看古今课文证据P10",
  "c4.use.sowing": "匹配预报农时与播种日期",
  "c4.use.zoning": "匹配农作物区划",
  "c4.use.forestry": "匹配造林与采种日期",
  "c4.use.introduction": "匹配植物引种",
  "c4.use.pest-control": "匹配虫害防治",
  "c4.use.mountain-observation": "匹配山区综合调查",
  "c4.process.read-p11": "阅读课文P11中的六项生产用途",
  "c4.process.observe": "完成六项生产用途观察",
  "c4.process.verify-p11-p12": "核验生产用途与研究意义",
  "c4.process.journal": "写入第四章生产观察册"
};

export interface AssistantSceneActivity {
  completedItemIds: string[];
  remainingItemIds: string[];
  controlValues: Record<string, AssistantSceneStateValue>;
}

function policyFor(sceneId: string): SceneStatePolicy {
  return ASSISTANT_SCENE_STATE_POLICIES[sceneId as AssistantStateSceneId] ?? {};
}

function labelsFor(sceneId: string): SceneStateLabels {
  return ASSISTANT_SCENE_STATE_LABELS[sceneId as AssistantStateSceneId] ?? {};
}

export function getAssistantSceneItemLabel(sceneId: string, itemId: string): string | undefined {
  return labelsFor(sceneId).items?.[itemId];
}

export function getAssistantRuleLabel(ruleId: string): string | undefined {
  return ASSISTANT_RULE_LABELS[ruleId];
}

export function getAssistantSceneControlLabel(sceneId: string, controlId: string): string | undefined {
  return labelsFor(sceneId).controls?.[controlId];
}

export function getAssistantSceneControlValueLabel(
  sceneId: string,
  controlId: string,
  value: AssistantSceneStateValue
): string {
  return labelsFor(sceneId).values?.[controlId]?.[String(value)] ?? String(value);
}

function recordValue(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function sanitizeControlValue(policy: ControlPolicy, value: unknown): AssistantSceneStateValue | undefined {
  if (policy.kind === "boolean") return typeof value === "boolean" ? value : undefined;
  if (policy.kind === "enum") {
    return typeof value === "string" && policy.values.includes(value) ? value : undefined;
  }
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  if (value < policy.min || value > policy.max) return undefined;
  if (policy.integer && !Number.isInteger(value)) return undefined;
  return value;
}

export function sanitizeAssistantControlValues(
  sceneId: string,
  value: unknown
): Record<string, AssistantSceneStateValue> {
  const controls = recordValue(value);
  const policies = policyFor(sceneId).controlValues ?? {};
  return Object.fromEntries(Object.entries(policies).flatMap(([controlId, policy]) => {
    const rawValue = Object.prototype.hasOwnProperty.call(controls, controlId)
      ? controls[controlId]
      : policy.defaultValue;
    const sanitized = sanitizeControlValue(policy, rawValue);
    return sanitized === undefined ? [] : [[controlId, sanitized]];
  }));
}

export function getAssistantSceneStateContextIds(sceneId: string): string[] {
  const policy = policyFor(sceneId);
  return [...new Set([
    ...(policy.selectedItemIds ?? []),
    ...(policy.placementItemIds ?? []),
    ...(policy.progressControlIds ?? []),
    ...Object.keys(policy.controlValues ?? {})
  ])];
}

export function getAssistantSceneProgressItemIds(sceneId: string): string[] {
  const policy = policyFor(sceneId);
  return [...new Set([
    ...(policy.selectedItemIds ?? []),
    ...(policy.placementItemIds ?? []),
    ...(policy.progressControlIds ?? [])
  ])];
}

export function getAssistantControlCompletedItemIds(sceneId: string, value: unknown): string[] {
  const policy = policyFor(sceneId);
  const controlValues = sanitizeAssistantControlValues(sceneId, value);
  const completedBooleans = (policy.progressControlIds ?? []).filter((controlId) =>
    controlValues[controlId] === true
  );
  const selectedValues = Object.values(controlValues).flatMap((controlValue) => {
    if (typeof controlValue !== "string") return [];
    const seenId = `seen:${controlValue}`;
    return (policy.progressControlIds ?? []).includes(seenId) ? [seenId] : [];
  });
  const currentTimelineStage = sceneId === "C2-00" && typeof controlValues.stage === "number"
    ? [`stage-${controlValues.stage}`]
    : [];
  return [...new Set([...completedBooleans, ...selectedValues, ...currentTimelineStage])];
}

export function summarizeAssistantSceneActivity(
  sceneId: string,
  visualState: unknown
): AssistantSceneActivity {
  const policy = policyFor(sceneId);
  const state = recordValue(visualState);
  const selected = Array.isArray(state.selectedItemIds) ? state.selectedItemIds : [];
  const selectedAllowlist = new Set(policy.selectedItemIds ?? []);
  const completedSelected = selected.filter((itemId): itemId is string =>
    typeof itemId === "string" && selectedAllowlist.has(itemId)
  );

  const placements = recordValue(state.placedItemIds);
  const placementItems = new Set(policy.placementItemIds ?? []);
  const placementTargets = new Set(policy.placementTargetIds ?? []);
  const completedPlacements = Object.entries(placements).flatMap(([itemId, targetId]) =>
    placementItems.has(itemId) && typeof targetId === "string" && placementTargets.has(targetId)
      ? [itemId]
      : []
  );

  const controlValues = sanitizeAssistantControlValues(sceneId, state.controlValues);
  const completedControls = getAssistantControlCompletedItemIds(sceneId, controlValues);
  const progressItemIds = getAssistantSceneProgressItemIds(sceneId);
  const completedItemIds = [...new Set([
    ...completedSelected,
    ...completedPlacements,
    ...completedControls
  ])];
  const completed = new Set(completedItemIds);

  return {
    completedItemIds,
    remainingItemIds: progressItemIds.filter((itemId) => !completed.has(itemId)),
    controlValues
  };
}
